import 'immer';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import type {
  Vendor,
  VendorSearchOutput,
  VendorSearchInput,
} from '@repo/api-contract';
import ApiClient from '../../util/api-client';
type SearchBy = VendorSearchInput['searchBy'];

export interface VendorSearchState {
  searchInput: string;
  openVendorSearchModal: boolean;
  resp?: VendorSearchOutput;
  searchBy: SearchBy;
  loading: boolean;
  searchLoading: boolean;
  selectedVendors: Vendor[];
  multiSelect: boolean;
  onImport?: (selectedVendors: Vendor[]) => void;
  onCancel?: () => void;
}

// Initialize state
const initialState: VendorSearchState = {
  searchInput: '',
  openVendorSearchModal: false,
  resp: undefined,
  searchBy: 'vendorName',
  loading: false,
  searchLoading: false,
  multiSelect: false,
  selectedVendors: [],
  onImport: undefined,
  onCancel: undefined,
};

export const VendorSearchModalSlice = createSlice({
  name: 'VendorSearch',
  initialState,
  reducers: {
    openSearchVendorModal: (
      state,
      action: PayloadAction<{
        onImport?: (selectedVendors: Vendor[]) => void;
        onCancel?: () => void;
        multiSelect?: boolean;
      }>,
    ) => ({
      ...initialState,
      openVendorSearchModal: true,
      onImport: action.payload.onImport,
      onCancel: action.payload.onCancel,
      multiSelect: action.payload.multiSelect || state.multiSelect,
    }),
    closeModal: (state) => ({
      ...state,
      openVendorSearchModal: false,
    }),
    setSearchInput: (state, action: PayloadAction<string>) => ({
      ...state,
      searchInput: action.payload,
    }),
    setSearchBy: (state, action: PayloadAction<SearchBy>) => ({
      ...state,
      searchBy: action.payload,
    }),
    setResults: (
      state,
      action: PayloadAction<VendorSearchOutput | undefined>,
    ) => ({
      ...state,
      resp: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setSearchLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      searchLoading: action.payload,
    }),
    setSelectedVendors: (state, action: PayloadAction<Vendor[]>) => {
      state.selectedVendors = action.payload;
    },
  },
});

export const {
  openSearchVendorModal,
  closeModal,
  setSearchInput,
  setSearchBy,
  setResults,
  setLoading,
  setSearchLoading,
  setSelectedVendors,
} = VendorSearchModalSlice.actions;
export default VendorSearchModalSlice.reducer;

export const searchVendors =
  (searchInput: VendorSearchInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setSearchLoading(true));
      const resp = await ApiClient.searchVendors.query(searchInput);
      dispatch(setResults(resp));
    } catch (error: unknown) {
      dispatch(setResults(undefined));
    } finally {
      dispatch(setSearchLoading(false));
    }
  };
